import { read, TABLE } from '../data/db';
import { hypotheses, matches, setPieces, teams } from '../data/repos';
import { deriveMatchMinute } from '../domain/clock';
import { isInMatch } from '../domain/machine';
import type { CaptureSetPieceCommand, CloseSetPieceCommand } from '../domain/commands';
import type { MatchRow, SetPieceRow, SetPieceType } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { newId } from './ids';

/**
 * SP-1 … SP-6 — the **I/O half** of the Set Piece sheet (build unit U-12).
 *
 * §1.2 forbids `ui/` from importing `data/`, so everything the sheet renders arrives through here and
 * every write leaves through `dispatch`. The *decisions* are not here: `domain/setPiece.ts` owns the
 * marker cycle and SP-5's geometry, `domain/commands/setPiece.ts` owns the guards, `domain/undo.ts`
 * owns the strip's target selection. This module reads, mints ids, derives the SES-10 minute and
 * dispatches.
 *
 * ── Why a Command and not a direct write ────────────────────────────────────
 *
 * `resume.recordSurface` writes straight through `write()` because it is per-context view state. A
 * set-piece capture is not: BAK-1 exports it, SYN-4 pushes it, DIST-4 attaches it to a talk point and
 * CARD-1 renders it. `InstallSeedTemplates` states the rule for exactly this case — *"`dispatch`
 * consults the §13.7 writer lock, and a second window that has gone read-only must not write to the
 * shared database"*. Every set-piece write therefore goes through the bus, in its own transaction,
 * with the same acknowledgment gating as a tap (SES-2).
 */

/** A hypothesis as §5.6's top-zone undo strip needs it: LIVE-6's predicate, plus what to name. */
export interface UndoStripRow {
  readonly id: string;
  readonly text: string;
  /** §5.4 — LIVE-6's window is a predicate over this committed column. */
  readonly lastIncrementTs: number | null;
}

export interface SetPieceContext {
  readonly match: MatchRow;
  /** §5.6's top zone shows *"clock · opponent · half"*, and the opponent is a name, not an id. */
  readonly opponent: string;
  /** SP-4 — the match's captures, in `[matchId+matchMinute]` order. Drafts included. */
  readonly captures: readonly SetPieceRow[];
  /** §5.6 — the undo strip's candidates. Not the sheet's content; the chrome's. */
  readonly undoRows: readonly UndoStripRow[];
}

/**
 * Everything the sheet needs, from **one** readonly transaction.
 *
 * One transaction because SP-4's per-category counts and SES-3's resumable draft are two reads of the
 * same table that must describe the same moment: a count taken before a draft write and a draft list
 * taken after it would render a sheet whose "2 defensive corners" disagrees with the capture it is
 * showing.
 *
 * The undo rows ride along rather than being a second load. §5.6 puts the strip in the top zone of
 * *this* surface, so it is part of what entering the sheet needs; fetching it separately would be a
 * second transaction on a navigation that already has one.
 *
 * @returns `undefined` when the match has vanished — a normal outcome after a cascade.
 * @throws {StorageError} on a read failure. Not swallowed: an empty sheet and a failed read look
 *   identical, and only one of them is worth trying again.
 */
export async function loadSetPieceContext(matchId: string): Promise<SetPieceContext | undefined> {
  return read(
    [TABLE.matches, TABLE.teams, TABLE.setPieces, TABLE.hypotheses],
    async (): Promise<SetPieceContext | undefined> => {
      const match = await matches.getMatch(matchId);
      if (match === undefined) return undefined;

      return {
        match,
        opponent: (await teams.getTeam(match.teamId))?.name ?? 'Opponent',
        captures: await setPieces.listForMatch(matchId),
        undoRows: (await hypotheses.listHypothesesForMatch(matchId)).map((row) => ({
          id: row.id,
          text: row.text,
          lastIncrementTs: row.lastIncrementTs,
        })),
      };
    },
    'loadSetPieceContext',
  );
}

/**
 * SP-4 — build the draft row, stamping `matchMinute` **at the moment the draft is created**.
 *
 * SES-10 derives the minute from the match's anchors through the one total function that produced
 * every other stored minute (INV-7, INV-10). It is stamped here, once, and
 * `handleCaptureSetPiece` refuses every later command that changes it — so *"stored at the moment the
 * draft is created"* is a property of the data rather than of the sheet remembering to resend the
 * same number.
 *
 * `at` is the moment the user started the capture, so a corner started at 62′ is a 62′ corner even if
 * the markers take until 63′ to place.
 */
export function newSetPieceDraft(match: MatchRow, type: SetPieceType, at: number): SetPieceRow {
  return {
    id: newId(),
    matchId: match.id,
    type,
    matchMinute: deriveMatchMinute(match, match.state, at),
    scheme: null,
    markers: [],
    flags: {},
    notes: null,
    draft: true,
  };
}

/**
 * Whether the sheet can capture anything right now.
 *
 * `isInMatch`, the same predicate the handler enforces and the same one `app/discovery.canCapture`
 * uses. The sheet renders in `preMatch` — SP-1 puts `[Set Piece]` in the top zone of a surface PRE-7
 * can hand the user before kickoff — and says the match has not started rather than swallowing taps.
 */
export function canCaptureSetPiece(match: MatchRow): boolean {
  return isInMatch(match.state);
}

/**
 * §9 — write the capture as it stands after this tap, and acknowledge **only** on commit.
 *
 * `onCommitted` is U-4's one acknowledgment point (§5.1 step 9): `dispatch` hands it to `write()`,
 * which invokes it synchronously inside the native `IDBTransaction` `complete` listener. A rejected
 * handler, an aborted transaction or a commit event that never arrives all leave it uncalled, so the
 * sheet never flashes a marker that did not persist — SES-2 on a surface that is not the hot path but
 * has the same rule.
 */
export function commitSetPiece(
  row: SetPieceRow,
  onCommitted?: (at: number) => void,
): Promise<DispatchResult> {
  const command: CaptureSetPieceCommand = { type: 'CaptureSetPiece', matchId: row.matchId, row };
  return dispatch(command, onCommitted ? { onCommitted } : {});
}

/** §9 — *"Exiting the sheet clears `draft`."* Idempotent; see `handleCloseSetPiece`. */
export function closeSetPiece(matchId: string, setPieceId: string): Promise<DispatchResult> {
  const command: CloseSetPieceCommand = { type: 'CloseSetPiece', matchId, setPieceId };
  return dispatch(command);
}
